// Users & roles — data access (ADR-021, invite-only access).
//
//   members      rpc tenant_members() — one request, ≤ 1000 rows ordered by email;
//                SECURITY DEFINER because emails live in auth.users (core.user.read)
//   invitations  user_invitations?select=<cols>&order=created_at.desc&limit=200
//                index: user_invitations_tenant_idx (tenant_id, created_at desc)
//   roles        roles?select=id,code,name,is_system&order=name — a handful of rows
//   writes       rpc invite_user / revoke_invitation / assign_role / unassign_role /
//                set_membership_status — the database enforces "no self-change,
//                no granting what you don't hold"; triggers write the audit log.
//
// Organizations have tens to hundreds of members, so each list is a single page
// (DataList still virtualizes the rows). Payload ≈ 150 B per member.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoUsersApi } from "./demo";

export interface MemberRow {
  user_id: string;
  email: string;
  full_name: string | null;
  status: "active" | "disabled";
  role_ids: string[];
  joined_at: string;
  last_sign_in_at: string | null;
  is_self: boolean;
  /** person, or agent (docs/design/agents.md); absent in demo data. */
  kind?: "person" | "agent";
}

export interface InvitationRow {
  id: string;
  email: string;
  full_name: string | null;
  role_id: string | null;
  status: "pending" | "accepted" | "revoked";
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
}

export interface RoleRow {
  id: string;
  code: string;
  name: string;
  is_system: boolean;
}

export interface InviteResult {
  invitation_id: string;
  /** Plain link token, returned once (the database keeps only its hash). */
  token: string;
  expires_at: string;
}

export type InvitationState = "pending" | "expired" | "accepted" | "revoked";

export function invitationState(inv: InvitationRow, now = Date.now()): InvitationState {
  if (inv.status === "pending" && new Date(inv.expires_at).getTime() <= now) return "expired";
  return inv.status;
}

export const userKeys = {
  all: ["users"] as const,
  members: () => [...userKeys.all, "members"] as const,
  invitations: () => [...userKeys.all, "invitations"] as const,
  roles: () => [...userKeys.all, "roles"] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

async function listMembers(signal: AbortSignal): Promise<MemberRow[]> {
  if (isDemoMode) return demoUsersApi.members();
  const { data, error } = await db().rpc("tenant_members").abortSignal(signal);
  if (error) throw error;
  return data as MemberRow[];
}

async function listInvitations(signal: AbortSignal): Promise<InvitationRow[]> {
  if (isDemoMode) return demoUsersApi.invitations();
  const { data, error } = await db()
    .from("user_invitations")
    .select("id,email,full_name,role_id,status,expires_at,created_at,accepted_at")
    .order("created_at", { ascending: false })
    .limit(200)
    .abortSignal(signal);
  if (error) throw error;
  return data as InvitationRow[];
}

async function listRoles(signal: AbortSignal): Promise<RoleRow[]> {
  if (isDemoMode) return demoUsersApi.roles();
  const { data, error } = await db().from("roles").select("id,code,name,is_system").order("name").abortSignal(signal);
  if (error) throw error;
  return data as RoleRow[];
}

// Single-page "infinite" queries so the shared DataList (virtualized, keyboard
// navigation) can render them.
export const memberListQuery = () =>
  infiniteQueryOptions({
    queryKey: userKeys.members(),
    queryFn: async ({ signal }) => ({ rows: await listMembers(signal), nextCursor: null as string | null }),
    initialPageParam: null as string | null,
    getNextPageParam: () => null,
    // Small and changed by other people (an invitee signing up): refetch in
    // the background whenever the tab is shown, keep the cached rows meanwhile.
    staleTime: 0,
  });

export const invitationListQuery = () =>
  infiniteQueryOptions({
    queryKey: userKeys.invitations(),
    queryFn: async ({ signal }) => ({ rows: await listInvitations(signal), nextCursor: null as string | null }),
    initialPageParam: null as string | null,
    getNextPageParam: () => null,
    // Small and changed by other people (an invitee signing up): refetch in
    // the background whenever the tab is shown, keep the cached rows meanwhile.
    staleTime: 0,
  });

export const roleListQuery = () =>
  queryOptions({
    queryKey: userKeys.roles(),
    queryFn: ({ signal }) => listRoles(signal),
    staleTime: 5 * 60_000,
  });

export async function inviteUser(email: string, roleId: string, fullName: string | null): Promise<InviteResult> {
  if (isDemoMode) return demoUsersApi.invite(email, roleId, fullName);
  const { data, error } = await db().rpc("invite_user", { p_email: email, p_role_id: roleId, p_full_name: fullName });
  if (error) throw error;
  return data as InviteResult;
}

export async function revokeInvitation(id: string): Promise<void> {
  if (isDemoMode) return demoUsersApi.revoke(id);
  const { error } = await db().rpc("revoke_invitation", { p_invitation_id: id });
  if (error) throw error;
}

export async function setMemberRole(userId: string, roleId: string, granted: boolean): Promise<void> {
  if (isDemoMode) return demoUsersApi.setRole(userId, roleId, granted);
  const { error } = await db().rpc(granted ? "assign_role" : "unassign_role", { p_user_id: userId, p_role_id: roleId });
  if (error) throw error;
}

export async function setMemberStatus(userId: string, status: "active" | "disabled"): Promise<void> {
  if (isDemoMode) return demoUsersApi.setStatus(userId, status);
  const { error } = await db().rpc("set_membership_status", { p_user_id: userId, p_status: status });
  if (error) throw error;
}

const knownErrors: [RegExp, string][] = [
  [/^You cannot (assign|remove) a role with permissions you do not hold/, "users.err.roleTooStrong"],
  [/^You cannot change a member who has permissions you do not hold/, "users.err.memberTooStrong"],
  [/^You cannot change your own/, "users.err.self"],
  [/^This person is already a member/, "users.err.alreadyMember"],
  [/^Enter a valid email address/, "users.err.email"],
  [/^Invitation limit reached/, "users.err.limit"],
  [/^Only a pending invitation can be revoked/, "users.err.notPending"],
  [/^You cannot change an invitation with permissions/, "users.err.invitationTooStrong"],
  [/^Missing permission/, "users.err.permission"],
];

export function userErrorKey(error: unknown): string | null {
  const message = (error as { message?: string } | null)?.message ?? "";
  return knownErrors.find(([re]) => re.test(message))?.[1] ?? null;
}

export function userErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string) => string;
  const key = userErrorKey(error);
  if (key) return tr(key);
  return (error as { message?: string } | null)?.message || tr("auth.errorGeneric");
}
